import api from './api';

import { satisfactionToNumeric, sqdDimensions } from '../components/common/constants';

const transformFormData = (formData) => {
  return {
    client_type: formData.clientType,
    gender: formData.gender,
    age: formData.age,
    region: formData.region,
    office: formData.office,
    sub_office: formData.subOffice,
    service: formData.service,
    other_specification: formData.otherSpecification,
    cc_awareness: formData.ccAwareness,
    cc_visibility: formData.ccVisibility,
    cc_helpfulness: formData.ccHelpfulness,
    sqd0: formData.satisfaction.sqd0,
    sqd1: formData.satisfaction.sqd1,
    sqd2: formData.satisfaction.sqd2,
    sqd3: formData.satisfaction.sqd3,
    sqd4: formData.satisfaction.sqd4,
    sqd5: formData.satisfaction.sqd5,
    sqd6: formData.satisfaction.sqd6,
    sqd7: formData.satisfaction.sqd7,
    sqd8: formData.satisfaction.sqd8,
    sqd9: formData.satisfaction.sqd9,
    sqd10: formData.satisfaction.sqd10,
    suggestions: formData.suggestions,
    email: formData.email,
    privacy_agreement: formData.privacyAgreement,
    timeSpent: formData.timeSpent,
    InternalExternal: formData.InternalExternal
  };
};


const calculateAverageSQD = (survey) => {
  // Get all SQD fields from sqd0 to sqd10
  const sqdFields = Array.from({ length: 11 }, (_, i) => `sqd${i}`);
  
  // Calculate the sum of all SQD values
  const sum = sqdFields.reduce((acc, field) => {
    // Skip N/A values in the calculation
    if (survey[field] === 'N/A') return acc;
    return acc + (Number(survey[field]) || 0);
  }, 0);
  
  // Count the number of valid (non-N/A) fields
  const validFields = sqdFields.filter(field => survey[field] !== 'N/A').length;
  
  // Return the average, rounded to 1 decimal place
  return validFields > 0 ? Math.round((sum / validFields) * 10) / 10 : 0;
};


// Survey API methods
const surveyApi = {
  /**
   * Get all surveys with optional params
   * @param {Object} params - Optional query parameters
   * @returns {Promise} API response
   */
  getSurvey: (params = null) => {
    return api.get('/surveys', params ? { params } : undefined)
      .then(response => {
        // Transform the response
        return {
          surveys: response.data.data,
          totalCount: response.data.data.total || response.data.total,
          percentageSatisfaction: response.data.data.percentageSatisfaction || response.data.percentageSatisfaction,
          averageTimeSpent: response.data.data.averageTimeSpent || response.data.averageTimeSpent
        };
      });
  },
  
  /**
   * Get surveys with date range filter
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Promise} API response
   */
  getSurveyWithDateRange: (startDate, endDate) => {
    return api.get('/surveys', {
      params: {
        start_date: startDate,
        end_date: endDate
      }
    });
  },
  
  /**
   * Get survey statistics
   * @returns {Promise} API response
   */
  getStats: () => {
    return api.get('/surveys/stats');
  },
  
  /**
   * Delete a survey
   * @param {number} id - Survey ID
   * @returns {Promise} API response
   */
  deleteSurvey: (id) => {
    return api.delete(`/surveys/${id}`);
  },
  
  /**
   * Get a specific survey by ID
   * @param {number} id - Survey ID
   * @returns {Promise} API response
   */
  getSurveyById: (id) => {
    return api.get(`/surveys/${id}`);
  },
  
  /**
   * Export surveys based on filters
   * @param {Object} filters - Filter parameters
   * @returns {Promise} API response
   */
  exportSurveys: (filters) => {
    return api.get('/surveys/export', {
      params: filters,
      responseType: 'blob'  // Important for downloading files
    });
  },
  
  
  /**
   * Submit a new survey
   * @param {Object} formData - Survey form data
   * @returns {Promise} API response
   */
  submitSurvey: async (formData) => {
    try { 
      const completionTimeSeconds = formData.timeSpent ? Math.round(formData.timeSpent / 1000) : null;
      const transformedData = transformFormData(formData);
      const response = await api.post('/surveys', transformedData);
      
      return response.data;
    } catch (error) {
      console.error('Error submitting survey:', error);
      
      // Format validation errors from Laravel to match React state structure
      if (error.response && error.response.status === 422) {
        const laravelErrors = error.response.data.errors;
        const formattedErrors = {};
        
        // Map Laravel error keys to React state keys
        Object.keys(laravelErrors).forEach(key => {
          // Convert snake_case to camelCase
          const reactKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
          formattedErrors[reactKey] = laravelErrors[key][0];
        });
        
        throw { validationErrors: formattedErrors };
      }
      
      throw error;
    }
  },

  getYearlyReport: (params = {}) => {
    // Adjust parameter naming to match the backend expectations
    const adjustedParams = {
      year: params.year,
      office: params.office || '',
      sub_office: params.subOffice || '',
      region: params.region || '',
      InternalExternal: params.InternalExternal
    };
  
    // Don't remove empty string parameters, only remove undefined/null
    Object.keys(adjustedParams).forEach(key => 
      (adjustedParams[key] === undefined || adjustedParams[key] === null) && delete adjustedParams[key]
    );
  
    console.log('Sending params to API:', adjustedParams);
  
    // Use the correct endpoint that matches the controller method
    return api.get('/surveys/generate-yearly-report', { params: adjustedParams })
      .then(response => {
        console.log('API response:', response.data);
        if (response.data && response.data.success) {
          // Transform the response to ensure consistent data structure
          return {
            data: {
              success: true,
              reportData: response.data.reportData || {},
              totalCount: response.data.totalCount || 0,
              percentageSatisfaction: response.data.percentageSatisfaction || 0
            }
          };
        } else {
          throw new Error('Invalid response format');
        }
      })
      .catch(error => {
        console.error('Error fetching yearly report:', error);
        
        // Log more details about the error
        if (error.response) {
          console.error('Error response data:', error.response.data);
          console.error('Error response status:', error.response.status);
        }
        
        // Handle potential validation or server errors
        if (error.response && error.response.status === 422) {
          const laravelErrors = error.response.data.errors;
          const formattedErrors = {};
          
          // Convert Laravel error keys to camelCase
          Object.keys(laravelErrors).forEach(key => {
            const reactKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
            formattedErrors[reactKey] = laravelErrors[key][0];
          });
          
          throw { validationErrors: formattedErrors };
        }
        
        // Return a consistent error response format
        return {
          data: {
            success: false,
            message: error.message || 'Failed to fetch report data'
          }
        };
      });
  }

};

export default surveyApi;